import React, { FunctionComponent, useCallback } from 'react';
import sliderTitleStyles from './sliderTitleStyles';
import { Box, Link as MuiLink, Typography, useTheme } from '@material-ui/core';
import clsx from 'clsx';
import { useHistory } from 'react-router';
import { routeUtils } from '../../../../../../common/utils/routeUtils';
import { ISpcTheme } from '../../../../../../common/theme/ISpcTheme';
import MarketingLabel from '../../../../../../common/components/marketingLabel/MarketingLabel';
import useIsMobile from '../../../../../../common/react/useIsMobile';
import Translation from '../../../../../../common/components/translation/Translation';
import TrimLevelName from '../../../../../../common/components/trimLevelsName/trimLevelName/TrimLevelName';
import StockStatus from '../stockStatus/StockStatus';
import usePlatformConfig from '../../../../../../common/react/usePlatformConfig';
import { StockVersionTypes } from '../../../../../../gqlCommon/enum/StockVersionTypes';
import { GetCustomizeCar_customizeCar_deliveryTimeFrame } from '../../../../../graphql/queries/__generated__/GetCustomizeCar';
import ShowIfTranslationExists from '../../../../../../common/components/ShowIfTranslationExists';
import IndicativeNumber from '../../../../../../common/components/indicativeNumber/IndicativeNumber';
import useScrollToSection from '../../../../../../customise/react/navigation/useScrollToSection';
import { CustomiseSections } from '../../../../../../common/enum/CustomiseSections';
import { ScrollInViewBehaviourType } from '../../../../../../common/service/scrollInView';

export interface ISliderTitle {
  title: string;
  marketingLabel?: string;
  hasStock?: boolean;
  stockLink?: {
    url: string;
    label: string;
  };
  deliveryTimeFrame?: GetCustomizeCar_customizeCar_deliveryTimeFrame;
  isInStock: boolean;
  isPreOrder: boolean;
  promotionalLabel?: string;
}

const SliderTitle: FunctionComponent<ISliderTitle> = ({
  title,
  marketingLabel,
  hasStock,
  stockLink,
  isInStock,
  isPreOrder,
  deliveryTimeFrame,
  promotionalLabel,
}) => {
  const styles = sliderTitleStyles();
  const {
    platformConfig: { stockVersion, showIndicativeDeliveryTimeframe },
  } = usePlatformConfig();
  const {
    spc: {
      customise: { hideStockLinkAvailability },
    },
  } = useTheme<ISpcTheme>();
  const history = useHistory();
  const isMobile = useIsMobile();
  const { scrollToSection } = useScrollToSection();

  const onIndicativeClick = useCallback(() => {
    scrollToSection(CustomiseSections.DISCLAIMER, ScrollInViewBehaviourType.AUTO);
  }, []);

  const showDeliveryTimeframe = !!deliveryTimeFrame?.deliveryTimeFrameLabel && showIndicativeDeliveryTimeframe;
  const showStockLink = !hideStockLinkAvailability && hasStock;

  return (
    <Box className={clsx([styles.container])}>
      <Box className={styles.nameContainer}>
        <TrimLevelName name={title} type="span" section="title" dataTest="trim_level_name:trim" />
        {!isMobile && <MarketingLabel content={marketingLabel} inline data-test="text:marketing_label" />}
      </Box>
      {stockVersion !== StockVersionTypes.NONE && <StockStatus isInStock={isInStock} isPreOrder={isPreOrder} />}
      {showDeliveryTimeframe && (
        <Typography component="span" className={styles.titleLabel} data-test="customise:deliveryTimeframe">
          <Translation
            id="trans__configurator__customise_page__delivery_time_frame"
            values={{ value: deliveryTimeFrame.deliveryTimeFrameLabel }}
          />
          <IndicativeNumber
            dataTest="delivery_time_frame"
            className={styles.indicative}
            displayNumber={<Translation id="trans__configurator__indicative_numbers__stock_estimated_delivery" />}
            onClick={onIndicativeClick}
          />
        </Typography>
      )}
      {showStockLink && (
        <ShowIfTranslationExists translationKey={stockLink.label}>
          <MuiLink
            data-test="customise:stock_link"
            className={styles.link}
            onClick={() => routeUtils.pushWithQueryParams(history, stockLink.url)}
          >
            <Translation id={stockLink.label} />
          </MuiLink>
        </ShowIfTranslationExists>
      )}
      {promotionalLabel && (
        <Typography component="span" className={styles.titleLabel} data-test="customise:promotionalLabel2">
          {promotionalLabel}
        </Typography>
      )}
    </Box>
  );
};

export default SliderTitle;
